import { getPrivateCacheScope, isCurrentPrivateCacheScope, subscribePrivateCacheScope } from './privateCacheScope.js';
/**
 * IndexedDB store for library metadata
 * Enables instant client-side filtering of large photo libraries (10,000+ images)
 */

const DB_NAME = 'dailey-library-metadata:v2';
const DB_VERSION = 1;
const STORE_NAME = 'imageMetadata';

let dbInstance = null;
subscribePrivateCacheScope(() => {
  dbInstance?.close();
  dbInstance = null;
});
function requireCurrentDatabase(db) {
  if (db !== dbInstance) throw new Error('Library cache identity changed');
}

/**
 * Open the IndexedDB database
 */
export async function openDatabase() {
  const snapshot = getPrivateCacheScope();
  if (!snapshot.scope) throw new Error('Library cache requires an authenticated user and tenant');
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(`${DB_NAME}:${snapshot.scope}`, DB_VERSION);

    request.onerror = () => {
      console.error('[libraryMetadataStore] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      if (!isCurrentPrivateCacheScope(snapshot)) {
        request.result.close();
        reject(new Error('Library cache identity changed'));
        return;
      }
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object store with image ID as key
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

        // Create indexes for filtering
        store.createIndex('file_extension', 'file_extension', { unique: false });
        store.createIndex('camera', 'camera', { unique: false });
        store.createIndex('lens', 'lens', { unique: false });
        store.createIndex('focal_length', 'focal_length', { unique: false });
        store.createIndex('iso', 'iso', { unique: false });
        store.createIndex('taken_at', 'taken_at', { unique: false });
        store.createIndex('is_favorite', 'is_favorite', { unique: false });
        store.createIndex('updated_at', 'updated_at', { unique: false });
      }
    };
  });
}

/**
 * Insert or update multiple image metadata records
 */
export async function upsertMetadata(images) {
  if (!images || images.length === 0) return;

  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    for (const image of images) {
      store.put(image);
    }
  });
}

/**
 * Get aggregated data for filter dropdowns
 * Returns distinct values with counts for each filterable field
 */
export async function getAggregations() {
  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const fileTypes = new Map();
    const cameras = new Map();
    const lenses = new Map();
    const focalBuckets = new Map();
    const isoBuckets = new Map();
    let focalMin = Infinity;
    let focalMax = -Infinity;
    let isoMin = Infinity;
    let isoMax = -Infinity;
    let dateMin = null;
    let dateMax = null;
    let totalImages = 0;

    const cursorRequest = store.openCursor();

    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;

      if (cursor) {
        const img = cursor.value;
        totalImages++;

        // File types
        if (img.file_extension) {
          const ext = img.file_extension;
          fileTypes.set(ext, (fileTypes.get(ext) || 0) + 1);
        }

        // Cameras
        if (img.camera) {
          cameras.set(img.camera, (cameras.get(img.camera) || 0) + 1);
        }

        // Lenses
        if (img.lens) {
          lenses.set(img.lens, (lenses.get(img.lens) || 0) + 1);
        }

        // Focal length range + distribution
        if (img.focal_length != null) {
          const fl = Math.round(img.focal_length);
          focalBuckets.set(fl, (focalBuckets.get(fl) || 0) + 1);
          focalMin = Math.min(focalMin, img.focal_length);
          focalMax = Math.max(focalMax, img.focal_length);
        }

        // ISO range + distribution
        if (img.iso != null) {
          isoBuckets.set(img.iso, (isoBuckets.get(img.iso) || 0) + 1);
          isoMin = Math.min(isoMin, img.iso);
          isoMax = Math.max(isoMax, img.iso);
        }

        // Date range
        if (img.taken_at) {
          if (!dateMin || img.taken_at < dateMin) dateMin = img.taken_at;
          if (!dateMax || img.taken_at > dateMax) dateMax = img.taken_at;
        }

        cursor.continue();
      } else {
        // Convert maps to sorted arrays
        const fileTypesArray = Array.from(fileTypes.entries())
          .map(([extension, count]) => ({
            extension,
            label: extension?.replace(/^\./, '').toUpperCase() || 'Unknown',
            count
          }))
          .sort((a, b) => b.count - a.count);

        const camerasArray = Array.from(cameras.entries())
          .map(([model, count]) => ({ model, count }))
          .sort((a, b) => b.count - a.count);

        const lensesArray = Array.from(lenses.entries())
          .map(([model, count]) => ({ model, count }))
          .sort((a, b) => b.count - a.count);

        resolve({
          file_types: fileTypesArray,
          cameras: camerasArray,
          lenses: lensesArray,
          ranges: {
            focal_length: {
              min: focalMin === Infinity ? null : focalMin,
              max: focalMax === -Infinity ? null : focalMax,
              distribution: Array.from(focalBuckets.entries())
                .map(([value, count]) => ({ value, count }))
                .sort((a, b) => a.value - b.value)
            },
            iso: {
              min: isoMin === Infinity ? null : isoMin,
              max: isoMax === -Infinity ? null : isoMax,
              distribution: Array.from(isoBuckets.entries())
                .map(([value, count]) => ({ value, count }))
                .sort((a, b) => a.value - b.value)
            },
            date: {
              min: dateMin,
              max: dateMax
            }
          },
          total_images: totalImages
        });
      }
    };

    cursorRequest.onerror = () => reject(cursorRequest.error);
  });
}

/**
 * Get filtered image IDs based on filter criteria
 * Returns array of image IDs matching all filters
 */
export async function getFilteredIds(filters = {}) {
  const db = await openDatabase();
  requireCurrentDatabase(db);
  const {
    fileTypes,
    camera,
    lens,
    focalMin,
    focalMax,
    isoMin,
    isoMax,
    dateFrom,
    dateTo
  } = filters;

  const hasFilters = (
    (fileTypes && fileTypes.length > 0) ||
    camera ||
    lens ||
    focalMin != null ||
    focalMax != null ||
    isoMin != null ||
    isoMax != null ||
    dateFrom ||
    dateTo
  );

  if (!hasFilters) {
    // No filters - return all IDs
    return getAllIds();
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const matchingIds = [];

    const fileTypeSet = fileTypes && fileTypes.length > 0
      ? new Set(fileTypes.map(t => t.toLowerCase()))
      : null;

    const cursorRequest = store.openCursor();

    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;

      if (cursor) {
        const img = cursor.value;
        let matches = true;

        // File type filter
        if (fileTypeSet) {
          const ext = (img.file_extension || '').toLowerCase();
          if (!fileTypeSet.has(ext) && !fileTypeSet.has(ext.replace(/^\./, ''))) {
            matches = false;
          }
        }

        // Camera filter
        if (matches && camera && img.camera !== camera) {
          matches = false;
        }

        // Lens filter
        if (matches && lens && img.lens !== lens) {
          matches = false;
        }

        // Focal length range
        if (matches && focalMin != null && (img.focal_length == null || img.focal_length < focalMin)) {
          matches = false;
        }
        if (matches && focalMax != null && (img.focal_length == null || img.focal_length > focalMax)) {
          matches = false;
        }

        // ISO range
        if (matches && isoMin != null && (img.iso == null || img.iso < isoMin)) {
          matches = false;
        }
        if (matches && isoMax != null && (img.iso == null || img.iso > isoMax)) {
          matches = false;
        }

        // Date range
        if (matches && dateFrom && (!img.taken_at || img.taken_at < dateFrom)) {
          matches = false;
        }
        if (matches && dateTo && (!img.taken_at || img.taken_at > dateTo)) {
          matches = false;
        }

        if (matches) {
          matchingIds.push(img.id);
        }

        cursor.continue();
      } else {
        resolve(matchingIds);
      }
    };

    cursorRequest.onerror = () => reject(cursorRequest.error);
  });
}

/**
 * Get all image IDs in the store
 */
export async function getAllIds() {
  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAllKeys();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get the most recent updated_at timestamp in the store
 * Used for incremental sync
 */
export async function getLastUpdatedAt() {
  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('updated_at');

    // Open cursor in reverse order to get the latest
    const request = index.openCursor(null, 'prev');

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        resolve(cursor.value.updated_at);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Get total count of images in the store
 */
export async function getCount() {
  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.count();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all data from the store
 * Used when user logs out or for full resync
 */
export async function clearAll() {
  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete specific image IDs from the store
 */
export async function deleteIds(ids) {
  if (!ids || ids.length === 0) return;

  const db = await openDatabase();
  requireCurrentDatabase(db);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    for (const id of ids) {
      store.delete(id);
    }
  });
}

/**
 * Check if IndexedDB is supported
 */
export function isSupported() {
  return typeof indexedDB !== 'undefined';
}

export const libraryMetadataStore = {
  openDatabase,
  upsertMetadata,
  getAggregations,
  getFilteredIds,
  getAllIds,
  getLastUpdatedAt,
  getCount,
  clearAll,
  deleteIds,
  isSupported
};
